import { useDatasets } from 'api/dataset/dataset.api'
import {
  useElplan,
  useElplanBids,
  useElplanInfo,
  useElplanSyncResultsMutation,
  useToggleLockMutation,
} from 'api/elplan/elplan.api'
import { resolveElplanBidsQueryEndTime } from 'api/elplan/elplan.bidQueryWindow.helper'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY, ELPLAN_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import { useUiConfigAnchorComponent } from 'api/uiConfig/uiConfig.api'
import { getElplanBidSumDataset } from 'helpers/elplan.helper/bidDataset.helper'
import { queryClient } from 'helpers/queryClient'
import React, { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import elplanStore from 'store/elplan/elplan'
import { Button, Card } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import UiConfig from 'ui/uiConfig/UiConfig'
import Datetime from 'utils/datetime/datetime'
import { getDayHourStarts } from 'utils/datetime/dayTimeSteps'
import { useSnapshot } from 'valtio'
import styles from './DayAheadPlan.module.less'
import type { DayAheadPlanAction } from './DayAheadPlan.types'
import type { ElplanEditorSession, ElplanSessionSource } from './ElplanEditor/ElplanEditor.types'
import { ElplanEditorModal } from './ElplanEditor/ElplanEditorModal'
import { openElplanEditorSession } from './ElplanEditor/session'
import { getDayAheadPlanActions, getHasBidsForDay } from './planActions'

type DayAheadPlanModalAction = Extract<DayAheadPlanAction['id'], 'empty' | 'fromBids' | 'edit'>

export default function DayAheadPlan(): ReactElement {
  const { t } = useTranslation()
  const auth = useAuth()
  const { data: elplanInfo } = useElplanInfo(auth.systemId)
  const elplanInfoId = elplanInfo?.id ?? 0

  const elplanSnap = useSnapshot(elplanStore)

  const [session, setSession] = useState<ElplanEditorSession | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<boolean>(false)

  const dayHourStarts = elplanSnap.date.start_time ? getDayHourStarts(elplanSnap.date.start_time) : []
  const dayStartTime = dayHourStarts.at(0)
  const lastHourStartTime = dayHourStarts.at(-1)
  const bidsQueryEndTime = lastHourStartTime && resolveElplanBidsQueryEndTime(lastHourStartTime)

  const { data: plan, refetch: refetchPlan } = useElplan(
    elplanInfoId,
    dayStartTime ? (Datetime.toLocalTime(dayStartTime, 'onlyDate') as ISODateTime) : undefined
  )

  const amountOfBlockBids = (elplanInfo?.bid_types ?? []).find((bidType) => bidType.type === 'blockbid')?.amount ?? 0
  const onlyReturnIds = new Array(amountOfBlockBids).fill('').map((_, i) => `blockbid.${i + 1}.volume`)

  const { data: elplanBidsForDay } = useElplanBids(elplanInfoId, dayStartTime, bidsQueryEndTime)
  const { data: elplanBidsUiConfig } = useUiConfigAnchorComponent('elplan_bids')
  const { data: elplanBidsDatasets = [] } = useDatasets(
    elplanBidsUiConfig?.id ?? -1,
    elplanBidsUiConfig?.version ?? -1,
    elplanBidsUiConfig?.dataset_instructions || [],
    dayStartTime,
    bidsQueryEndTime,
    {
      onlyReturnIds,
      fillMissingHourTimestamps: false,
      ensureUniformTimeIntervals: false,
      datasetRefreshToken: ELPLAN_BID_REFRESH_EVENT_QUERY_KEY,
      overrideAlias: {
        elplan_info_id: elplanInfoId,
      },
    }
  )

  const bidSumMwDataset = getElplanBidSumDataset(elplanBidsDatasets, amountOfBlockBids)

  const elplanExistsForCurrentDate = Boolean(plan)
  const isLocked = plan?.is_locked ?? false
  const elplanId = plan?.id ?? 0

  const actions = getDayAheadPlanActions({
    elplanInfo,
    plan: dayStartTime ? plan : undefined,
    hasBidsForDay: getHasBidsForDay(elplanBidsForDay, bidSumMwDataset),
  })
  const fromBidsAction = actions.find((action) => action.id === 'fromBids')

  function isOffered(id: DayAheadPlanAction['id']): boolean {
    return actions.some((action) => action.id === id)
  }

  function getSessionSource(action: DayAheadPlanModalAction): ElplanSessionSource | null {
    switch (action) {
      case 'empty':
        return { kind: 'empty', elplanInfoId }
      case 'fromBids':
        return { kind: 'fromBids', elplanInfoId, bidSumMwDataset }
      case 'edit':
        return plan ? { kind: 'edit', elplan: plan } : null
    }
  }

  function openSession(action: DayAheadPlanModalAction): void {
    const source = getSessionSource(action)
    const openedSession = source && dayStartTime ? openElplanEditorSession(source, dayStartTime) : null
    if (!openedSession) {
      alertStore.error(t('The electricity plan for this day cannot be opened'))
      return
    }
    setSession(openedSession)
  }

  const { mutate: toggleLockVerification } = useToggleLockMutation()
  const { mutate: syncElplanResults } = useElplanSyncResultsMutation()

  // Invalidate queries on mount to ensure fresh data when component initializes
  // Child hooks (useElplanBids, useDatasets) handle their own refetching when date changes
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY] })
  }, [])

  const onClickToggleLock = isLocked ? () => toggleLockVerification({ id: elplanId }) : () => setConfirmDialog(true)

  const onConfirmLockClick = () => {
    setConfirmDialog(false)
    toggleLockVerification({ id: elplanId })
  }

  return (
    <>
      <Card title={t(`Day ahead plan`)}>
        <div className={styles.DayAheadPlan_Period}>
          {elplanExistsForCurrentDate && (
            <InfoBanner
              text={
                isLocked
                  ? t('The electricity plan is locked in production plan')
                  : t(
                      'As of now, this electricity plan does not affect the production plan. Lock the electricity plan with the button on the right'
                    )
              }
              style={!isLocked ? 'warning' : 'info'}
              variant={!isLocked ? 'textWithBackground' : 'text'}
            />
          )}
        </div>

        <div className={styles.DayAheadPlan_Buttons__right}>
          {isOffered('fetchResults') && (
            <Button
              secondary
              marginBottom
              icon={'fal fa-sync'}
              onClick={() => {
                if (!auth.activeSystem?.name || !elplanSnap.date.start_time) {
                  return
                }

                syncElplanResults({
                  date: Datetime.toLocalTime(elplanSnap.date.start_time, 'onlyDate'),
                  system: auth.activeSystem?.name,
                  systemId: auth.activeSystem.id,
                })
              }}
            >
              {t('Fetch results')}
            </Button>
          )}
          {isOffered('empty') && (
            <Button primary icon={'fal fa-plus'} marginLeft marginBottom onClick={() => openSession('empty')}>
              {t('Create empty electricity plan')}
            </Button>
          )}
          {fromBidsAction && (
            <Button
              primary
              icon={'fal fa-plus'}
              marginLeft
              marginBottom
              disabled={fromBidsAction.disabledReason !== undefined}
              disabledTooltip={fromBidsAction.disabledReason}
              onClick={() => openSession('fromBids')}
            >
              {t('Create electricity plan from bids')}
            </Button>
          )}
          {isOffered('edit') && (
            <Button icon={'fal fa-pen'} primary marginBottom marginLeft onClick={() => openSession('edit')}>
              {t('Edit electricity plan')}
            </Button>
          )}
          {isOffered('lock') && (
            <Button
              icon={isLocked ? 'fal fa-unlock' : 'fal fa-lock'}
              primary={!isLocked}
              warning={isLocked}
              marginLeft
              marginBottom
              tooltip={isLocked ? t('Electricitiy plan will no longer be locked in production plan') : ''}
              onClick={onClickToggleLock}
            >
              {isLocked ? t('Unlock to edit') : t('Lock in production plan')}
            </Button>
          )}
        </div>

        <div className={styles.DayAheadPlan_Tables}>
          <UiConfig
            type="day_ahead_overview"
            datasetStartTime={elplanSnap.date.start_time}
            datasetEndTime={elplanSnap.date.end_time}
            datasetRefreshToken={ELPLAN_REFRESH_EVENT_QUERY_KEY}
          />
        </div>
      </Card>

      {session && (
        <ElplanEditorModal
          session={session}
          refetchPlan={() => refetchPlan({ cancelRefetch: false })}
          onClose={() => setSession(null)}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog
          open={confirmDialog}
          onConfirm={onConfirmLockClick}
          onClose={() => setConfirmDialog(false)}
          textObject={{
            title: t(`Are you sure you want to lock a manual electric plan?`),
            text: t(
              `Note that if you lock a manual electricity plan, the automatic electricity plan will not automatically enter. Do you want to continue to lock the electricity plan?`
            ),
          }}
        />
      )}
    </>
  )
}
